import { Injectable } from '@angular/core';
import { Observable} from 'rxjs';
import { Subject, from, throwError, BehaviorSubject } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { HttpClient} from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import  { jwtDecode }  from 'jwt-decode';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult } from '@azure/msal-browser';
import { Batch, Page, PageDetails, ReprocessResponse, ClientLookupResponse, SearchClientResponse } from '../models/batch.interface';
import { environment } from '../../environments/environment';
import { JwtPayload } from '../models/jwt--payload.model';

@Injectable({
  providedIn: 'root'
})
export class BatchService {
 
  // Observable to track user's login status
  isUserLoggedIn: Subject<boolean> = new Subject<boolean>();
  public batches: Batch[] = [];
  selectedConnector: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  // Stores the access token once retrieved
  private token: string | null = null;

  constructor(
    private http: HttpClient,
    private msalService: MsalService,
  ) {}

  // Retrieves access token silently using MSAL; caches it for reuse
  private isTokenExpired(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const currentTime = Math.floor(Date.now() / 1000);
      return decoded.exp < currentTime;
    } catch (error) {
      console.error('Failed to decode token:', error);
      return true; // Treat as expired if decoding fails
    }
  }

  private getAccessToken(): Observable<string> {
    if (this.token && !this.isTokenExpired(this.token)) {
      return from(Promise.resolve(this.token));
    }

    const activeAccount = this.msalService.instance.getActiveAccount();
    if (!activeAccount) {
      console.error('No active account! Please log in.');
      return throwError(() => new Error('No active account! Please log in.'));
    }

    return from(
      this.msalService.acquireTokenSilent({
        scopes: environment.scopes,
        account: activeAccount,
      }),
    ).pipe(
      map((result: AuthenticationResult) => {
        this.token = result.idToken;
        return result.idToken;
      }),
      catchError((error) => {
        console.error('Error acquiring token:', error);
        return throwError(() => error);
      }),
    );
  }

  // Creates HTTP headers with authorization and function key
  private createHeaders(accessToken: string, opts?: { omitContentType: boolean; }): HttpHeaders {
    let headers = new HttpHeaders().set('Authorization', `Bearer ${accessToken}`);

    // For JSON requests we keep Content-Type: application/json
    // For multipart uploads we MUST NOT set Content-Type; the browser will set it with the boundary.
    if (!opts?.omitContentType) {
      headers = headers.set('Content-Type', 'application/json');
    }
    return headers;
  }

  // Fetches all batches for a given connector
  
public getAllBatches(connector: string): Observable<Batch[]> {
    return this.getAccessToken().pipe(
      switchMap((accessToken) => {
        const headers = this.createHeaders(accessToken);
        const params = new HttpParams().set('connector', connector);
        return this.http
          .get<Batch[]>(`${environment.apiBaseUrl}/batches`, {
            headers,
            params,
          })
          .pipe(
            map((response) => {
              return response;
            }),
            catchError((error) => {
              console.error('Error fetching batches:', error);
              if (error.status === 401) {
                console.error(
                  'Unauthorized: Check if the token is valid and not expired.',
                );
              }
              return throwError(
                () =>
                  new Error('Failed to fetch batches. Please try again later.'),
              );
            }),
          );
      }),
    );
  }


  // Fetches batches filtered by connector
  public getBatchesByConnector(connector: string): Observable<Batch[]> {
    return this.getAccessToken().pipe(
      switchMap((accessToken) => {
        const headers = this.createHeaders(accessToken);
        const params = new HttpParams().set('connector', connector);
        return this.http
          .get<Batch[]>(`${environment.apiBaseUrl}/batches`, {
            headers,
            params,
          })
          .pipe(
            map((response) =>  response),
            catchError((error) => {
              console.error('Error fetching batches:', error);
              return throwError(
                () =>
                  new Error('Failed to fetch batches. Please try again later.'),
              );
            }),
          );
      }),
    );
  }

  public getBatchById(batchId: string): Observable<Batch> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token);
        const params = new HttpParams().set('batchId', batchId);
        return this.http.get<Batch>(`${environment.apiBaseUrl}/batch`, {
          headers,
          params,
        });
      }),
      catchError(() => {
        return throwError(
          () =>
            new Error('Failed to fetch page details. Please try again later.'),
        );
      }),
    );
  }

  public getAllPages(batchId: string): Observable<Page[]> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token);
        return this.http.get<Page[]>(
          `${environment.apiBaseUrl}/${batchId}/pages`,
          { headers },
        );
      }),
      catchError(() => {
        return throwError(
          () => new Error('Failed to fetch pages. Please try again later.'),
        );
      }),
    );
  }

  public getPageById(batchId: string, pageId: string): Observable<PageDetails> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token);
        return this.http.get<PageDetails>(
          `${environment.apiBaseUrl}/${batchId}/page/${pageId}`,
          {
            headers,
          },
        );
      }),
      catchError(() => {
        return throwError(
          () =>
            new Error('Failed to fetch page details. Please try again later.'),
        );
      }),
    );
  }  
  public updateBatch(batchId: string, body: Batch): Observable<Batch> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token).set(
          'Content-Type',
          'application/json',
        );
        const url = `${environment.apiBaseUrl}/batch/${batchId}?batchId=${batchId}`;
        return this.http.put<Batch>(url, body, { headers });
      }),
      catchError(() => {
        return throwError(
          () => new Error('Failed to update batch. Please try again later.'),
        );
      }),
    );
  }


  // Reprocess a batch using its batchId
  public reprocessBatch(batchId: string): Observable<ReprocessResponse> {
    return this.getAccessToken().pipe(
      switchMap((accessToken) => {
        const headers = this.createHeaders(accessToken);
        const url = `${environment.apiBaseUrl}/${batchId}/reprocessBatch`;
        return this.http.get<ReprocessResponse>(url, { headers });
      }),
    );
  }

      // Call export API for a batch which performs validation and finalises export
    public exportBatch(batchId: string, body: Record<string, unknown>): Observable<unknown> {
      return this.getAccessToken().pipe(
        switchMap((token) => {
          const headers = this.createHeaders(token).set('Content-Type', 'application/json');
          const url = `${environment.apiBaseUrl}/${batchId}/export`;
          let params = new HttpParams();
          try {
            const connector = body?.['connector'];
            if (typeof connector === 'string' && connector) {
              params = params.set('connector', connector);
            }
          } catch (e) { /* ignore */ }
          return this.http.post<unknown>(url, body, { headers, params });
        }),
        catchError(() => {
          return throwError(() => new Error('Failed to export batch. Please try again later.'));
        })
      );
    }

  public deleteBatch(batchId: string): Observable<void> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token);
        const params = new HttpParams().set('batchId', batchId);
        const url = `${environment.apiBaseUrl}/batch`;
        return this.http.delete<void>(url, { headers, params }).pipe(
          catchError((error) => {
            console.error('Failed to delete batch:', {
              url,
              params: params.toString(),
              status: error?.status,
              error: error?.error,
            });
            return throwError(() => error);
          }),
        );
      }),
    );
  }

  public getClientByGCID(gcid: string): Observable<ClientLookupResponse> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token);
        return this.http.get<ClientLookupResponse>(
          `${environment.apiBaseUrl}/findClientByGCID/${gcid}`,
          { headers }
        );
      }),
      catchError(() => {
        return throwError(
          () => new Error('Failed to fetch client details. Please try again later.')
        );
      })
    );
  }
  
  public searchClientByISB(isbNumber: string): Observable<SearchClientResponse[]> {
  // Sanitize ISB: trim and remove any leading zeros before sending to API
  const sanitizedIsb = (isbNumber || '').toString().trim().replace(/^0+/, '');

  const body = {
    SearchCriteria: {
      Key: [
        { AttrCode: 'Key KeyType', Value: 'ISB' },
        { AttrCode: 'Key KeyValue', Value: sanitizedIsb }
      ]
    }
  };

  return this.getAccessToken().pipe(
    switchMap((token) => {
      const headers = this.createHeaders(token);
      return this.http.post<SearchClientResponse[]>(
        `${environment.apiBaseUrl}/searchClientByAttribute`,
        body,
        { headers }
      );
    }),
    catchError(() => {
      return throwError(() => new Error('Failed to search client by ISB.'));
    })
  );
}
  // Upload document API integration
   public uploadDocument(
    files: File[],
    uploaderEmail: string,
    metadata?: Record<string, string>,
    documentType?: string,
  ): Observable<unknown> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token, { omitContentType: true }).set(
          'X-User-Email',
          uploaderEmail,
        );
        const form = new FormData();
        for (const file of files) {
          form.append('files', file, file.name);
        }
        form.append('uploaderEmail', uploaderEmail);
        if (metadata) {
          form.append('metadata', JSON.stringify(metadata));
        }
        // If metadata contains a connector we forward it as the use-case in the path
        const connector = metadata?.['connector'];
        const useCase = connector ? encodeURIComponent(connector) : null;
        let url = useCase
          ? `${environment.apiBaseUrl}/uploadDocument/${useCase}`
          : `${environment.apiBaseUrl}/uploadDocument`;
        if (documentType) {
          url += `?documentType=${encodeURIComponent(documentType)}`;
        }
        try {
        } catch (e) {
        }
        return this.http.post(url, form, { headers, reportProgress: true, observe: 'events' });
      }),
    );
  }
    
  // Search clients by full/partial legal name
  public searchClientByAllLegalName(legalName: string): Observable<SearchClientResponse[]> {
    const body = { LegalName: (legalName || '').toString().trim() };

    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = this.createHeaders(token);
        return this.http.post<SearchClientResponse[]>(
          `${environment.apiBaseUrl}/searchClientByLegalName`,
          body,
          { headers }
        );
      }),
      catchError((error) => {
        console.error('Failed to search client by legal name:', error);
        return throwError(() => new Error('Failed to search client by legal name.'));
      })
    );
  }

  // Call export API for a batch which performs validation and finalises export
  public exportVcfBatch(batchId: string): Observable<Blob> {
  return this.getAccessToken().pipe(
    switchMap(token => {
      const headers = this.createHeaders(token);
      const url = `${environment.apiBaseUrl}/vcf/export?batchIds=${batchId}`;
      return this.http.get(url, { headers, responseType: 'blob' });
    })
  );
  }
}
